import { useNNEvidences } from "@/api/nn-evidence";
import { useRFS } from "@/api/rfs";
import { Checking, ContainerPrimary, ContainerSecondary } from "../ui";
import { DownloadButton } from "@/components/ui/button";
import { Anchor } from "@/components/ui/anchor";
import { useDocumentDownload } from "../utils/useDocumentDownload";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/helper/formatDate";
import { DataInfoWithBadge } from "@/components/ui/data-info";
import { useRegeneratePolling } from "../../hooks/useRegeneratePolling";
import { showDummyHouseNumbersAlert } from "../utils/validation";

export function EvidenceNNHandler({
  id,
  hasRequiredAsterisk = false,
}: {
  id: string;
  hasRequiredAsterisk?: boolean;
}) {
  const { data: rfs, isPending: isLoadingRFS } = useRFS({ id });
  const { currentStatus } = useRegeneratePolling({ id });
  const { data: nnEvidences, isPending: isLoadingNnEvidences } = useNNEvidences(
    { rfsId: id },
  );
  const { download, isDownloading } = useDocumentDownload();

  if ((nnEvidences ?? []).length > 1) {
    throw new Error("Multiple NN Evidence found");
  }

  const nnEvidence = nnEvidences?.[0];
  const isValidated = rfs?.evidenceNnStatus === "Validated";

  const isShowDummyAlert = showDummyHouseNumbersAlert(nnEvidence);

  const shouldShowCreateButton = currentStatus == "SUCCESS";

  function handleDownload() {
    download({
      path: nnEvidence?.documentNnEvidencePath ?? "",
      fileName: `EVIDENCE-${rfs?.clusterId}.kmz`,
    });
  }

  return (
    <ContainerPrimary>
      <div className="flex flex-grow flex-col whitespace-nowrap">
        <span>Evidence NN{hasRequiredAsterisk && <RequiredAsterisk />}</span>
        {isShowDummyAlert && (
          <span>
            <Badge variant={"success"}>No dummy house numbers</Badge>
          </span>
        )}
      </div>
      <ContainerSecondary className="w-fit flex-none gap-x-4">
        {isLoadingNnEvidences || isLoadingRFS ? (
          <Checking />
        ) : (
          <>
            <div className="flex flex-col items-center text-center text-xs">
              <span className="text-center font-medium">
                Doc Uncompleted Date
              </span>
              <span className="text-center">
                {rfs?.evidenceNnUncompletedDate
                  ? formatDateTime(rfs?.evidenceNnUncompletedDate)
                  : "-"}
              </span>
            </div>
            <div className="flex flex-col items-center text-center text-xs">
              <span className="text-center font-medium">
                Doc Completed Date
              </span>
              <span className="text-center">
                {rfs?.evidenceNnCompletedDate
                  ? formatDateTime(rfs?.evidenceNnCompletedDate)
                  : "-"}
              </span>
            </div>

            <DataInfoWithBadge
              value={
                rfs?.evidenceNnIsSubmitted === true
                  ? "Submitted"
                  : "Not Submitted"
              }
            />
            <DataInfoWithBadge value={rfs?.evidenceNnStatus ?? "Unvalidated"} />

            {isValidated && (
              <DownloadButton
                onClick={handleDownload}
                loading={isDownloading}
              />
            )}
            <Anchor
              mood="axiata-blue-outline"
              href={`/rfs/rfs/${id}/nn-evidence/create`}
              state={{ from: `/rfs/rfs/${id}?tab=rfs-process` }}
              className={shouldShowCreateButton ? "visible" : "invisible"}
            >
              {!isValidated ? <p>Create</p> : <p>Detail</p>}
            </Anchor>
          </>
        )}
      </ContainerSecondary>
    </ContainerPrimary>
  );
}
